import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/PageHeader";
import { brand } from "@/data/content";

const TERMS_SECTIONS = [
  {
    heading: "1. Use of the Website",
    paragraphs: ["You may use this website only for lawful and legitimate purposes.", "You agree not to:"],
    bullets: [
      "Use the website for any unlawful, fraudulent, or unauthorised purpose.",
      "Attempt to gain unauthorised access to the website, servers, systems, or databases.",
      "Interfere with the operation, security, or functionality of the website.",
      "Introduce viruses, malicious code, or other harmful material.",
      "Copy, reproduce, modify, distribute, or commercially exploit website content without prior written permission.",
      "Use our branding, content, creative work, or intellectual property in a way that may create confusion, impersonation, or damage to HashTag Marketing.",
    ],
  },
  {
    heading: "2. Intellectual Property",
    paragraphs: [
      "Unless otherwise stated, all content available on this website belongs to or is licensed to HashTag Marketing.",
      "This includes, but is not limited to:",
    ],
    bullets: [
      "Logos and brand identity",
      "Text and written content",
      "Graphics and illustrations",
      "Photographs and videos",
      "Creative concepts",
      "Campaign-related materials",
      "Presentations",
      "Website design and layouts",
      "Marketing materials",
      "Other original creative and intellectual property",
    ],
    after: ["No part of this website may be copied, reproduced, modified, published, distributed, licensed, sold, or commercially exploited without our prior written permission."],
  },
  {
    heading: "3. Information About Our Services",
    paragraphs: [
      "The information presented on our website regarding our capabilities, services, expertise, processes, campaigns, packages, or solutions is provided for general informational purposes.",
      "Nothing on this website constitutes a binding proposal, quotation, offer, or agreement to provide services.",
      "Specific services, deliverables, timelines, fees, revisions, responsibilities, usage rights, intellectual property rights, confidentiality obligations, and other commercial terms will be governed by the applicable proposal, quotation, statement of work, service agreement, or other written agreement between HashTag Marketing and the client.",
    ],
  },
  {
    heading: "4. Marketing and Campaign Performance",
    paragraphs: [
      "HashTag Marketing works across social media, digital marketing, PR, influencer marketing, branding, content, websites, and other marketing channels.",
      "Marketing performance can be influenced by factors beyond our control, including:",
    ],
    bullets: [
      "Platform algorithms",
      "Changes to social media platforms",
      "Audience behaviour",
      "Market conditions",
      "Competition",
      "Advertising policies",
      "Third-party platform decisions",
      "Technical issues",
      "Changes in trends or consumer behaviour",
    ],
    after: ["Unless expressly guaranteed in a written agreement, HashTag Marketing does not guarantee specific levels of reach, engagement, impressions, followers, media coverage, leads, conversions, sales, website traffic, or revenue."],
  },
  {
    heading: "5. Influencer and PR Activities",
    paragraphs: [
      "HashTag Marketing may coordinate campaigns involving influencers, creators, celebrities, media publications, journalists, vendors, platforms, and other third parties.",
      "While we may manage or facilitate such activities, third-party participation, availability, publication decisions, audience behaviour, content performance, reach, engagement, or media coverage may be outside our control.",
      "Any specific commitments or deliverables will be governed by the applicable client or campaign agreement.",
    ],
  },
  {
    heading: "6. Website Development and Branding Services",
    paragraphs: [
      "Where HashTag Marketing provides website development, design, branding, creative, or related services, ownership, licensing, usage rights, source files, third-party assets, and deliverables will be determined by the applicable client agreement.",
      "Third-party fonts, stock images, software, plugins, templates, platforms, integrations, or other licensed materials may be subject to their own terms and usage restrictions.",
    ],
  },
  {
    heading: "7. Portfolio and Client Work",
    paragraphs: [
      "HashTag Marketing may showcase selected campaigns, creative work, collaborations, projects, testimonials, or client work across our website, social media, presentations, and other marketing materials, subject to applicable permissions and contractual obligations.",
      "The ownership and permitted use of specific client work will be determined by the applicable agreement.",
    ],
  },
  {
    heading: "8. Third-Party Websites and Services",
    paragraphs: [
      "Our website may contain links to third-party websites, platforms, or services.",
      "Such links are provided for convenience and do not necessarily represent an endorsement or recommendation.",
      "HashTag Marketing is not responsible for the availability, accuracy, content, security, privacy practices, or policies of third-party websites or services.",
    ],
  },
  {
    heading: "9. Website Availability",
    paragraphs: [
      "We aim to keep our website accessible and functional; however, we do not guarantee that the website will always be available, uninterrupted, secure, or free from errors or technical issues.",
      "We may suspend, modify, update, or discontinue any part of the website without prior notice.",
    ],
  },
  {
    heading: "10. Disclaimer",
    paragraphs: [
      "Information available on this website is provided for general informational purposes.",
      "While HashTag Marketing makes reasonable efforts to maintain accurate and useful information, we do not guarantee that all content will always be complete, accurate, current, or error-free.",
    ],
  },
  {
    heading: "11. Limitation of Liability",
    paragraphs: [
      "To the maximum extent permitted by applicable law, HashTag Marketing shall not be liable for any direct, indirect, incidental, consequential, or other loss arising from your use of, or inability to use, this website or reliance on information provided through it.",
      "Nothing in these Terms excludes or limits any liability that cannot legally be excluded or limited under applicable law.",
    ],
  },
  {
    heading: "12. Changes to These Terms",
    paragraphs: [
      "HashTag Marketing may modify these Terms & Conditions from time to time.",
      "Any revised terms will be published on this page with an updated \u201cLast Updated\u201d date.",
      "Your continued use of the website after such changes are published constitutes acceptance of the revised Terms, to the extent permitted by applicable law.",
    ],
  },
  {
    heading: "13. Governing Law",
    paragraphs: [
      "These Terms & Conditions shall be governed by and interpreted in accordance with the applicable laws of India.",
      "Any dispute arising in connection with these Terms or your use of the website shall be subject to the jurisdiction of the courts of Gurgaon, Haryana, India, unless otherwise agreed in writing or required by applicable law.",
    ],
  },
  {
    heading: "14. Contact Us",
    paragraphs: ["For questions regarding these Terms & Conditions, please contact:"],
    contact: true,
  },
];

const PRIVACY_SECTIONS = [
  {
    heading: "1. Information We Collect",
    paragraphs: [
      "We may collect information that you voluntarily provide to us when you interact with our website or contact us.",
      "This may include:",
    ],
    bullets: [
      "Full name",
      "Email address",
      "Phone number",
      "Company or organisation name",
      "Job title or designation",
      "Business or brand information",
      "Marketing and communication requirements",
      "Project or campaign-related information",
      "Information submitted through contact, enquiry, consultation, or other forms",
      "Any other information you voluntarily provide to us",
    ],
    after: ["We may also automatically collect certain technical information when you visit our website, including:"],
    bullets2: [
      "IP address",
      "Browser type and version",
      "Device type",
      "Operating system",
      "Website pages visited",
      "Time spent on our website",
      "Referring website or source",
      "General website usage and interaction information",
    ],
  },
  {
    heading: "2. How We Use Your Information",
    paragraphs: ["We may use the information we collect for purposes including:"],
    bullets: [
      "Responding to enquiries and requests.",
      "Understanding your brand, business, and marketing requirements.",
      "Preparing proposals, quotations, presentations, or recommendations.",
      "Communicating with you about our services.",
      "Providing and managing our services.",
      "Planning and executing marketing, branding, PR, influencer, digital, and creative campaigns.",
      "Improving our website, services, content, and user experience.",
      "Conducting internal business analysis and research.",
      "Sending relevant business updates, service information, or marketing communications where permitted.",
      "Maintaining the security and functionality of our website.",
      "Complying with applicable legal and regulatory requirements.",
    ],
  },
  {
    heading: "3. Marketing Communications",
    paragraphs: [
      "If you provide your contact information to HashTag Marketing, we may contact you regarding our services, proposals, campaigns, events, business updates, or other information that may be relevant to you.",
      "You may opt out of promotional communications at any time by contacting us or using the unsubscribe option provided in the relevant communication, where applicable.",
    ],
  },
  {
    heading: "4. Sharing of Information",
    paragraphs: [
      "HashTag Marketing does not sell or rent your personal information.",
      "We may share information with trusted third parties where reasonably necessary to operate our business or provide our services.",
      "These may include:",
    ],
    bullets: [
      "Technology and software providers",
      "Website and hosting providers",
      "Analytics providers",
      "Advertising platforms",
      "Influencers and creators",
      "Media and PR partners",
      "Freelancers, consultants, and creative partners",
      "Vendors and service providers",
      "Other campaign or business partners",
    ],
    after: ["We may also disclose information where required by law, regulation, legal proceedings, or governmental authorities, or where reasonably necessary to protect our rights, business, users, or property."],
  },
  {
    heading: "5. Client and Campaign Information",
    paragraphs: [
      "As part of our services, clients may provide HashTag Marketing with business information, brand assets, campaign briefs, customer or audience information, creative materials, contact information, or other data.",
      "Such information will be used for the purposes for which it has been provided and in accordance with applicable agreements, client instructions, and applicable law.",
      "Where HashTag Marketing processes information on behalf of a client, the relevant client agreement and applicable privacy requirements may also apply.",
    ],
  },
  {
    heading: "6. Third-Party Platforms",
    paragraphs: [
      "Our services may involve third-party platforms and technologies, including social media platforms, advertising platforms, analytics tools, influencer platforms, website technologies, email platforms, communication tools, and other digital services.",
      "These third parties may independently collect and process information according to their own privacy policies and terms.",
      "HashTag Marketing does not control the privacy practices of third-party platforms.",
    ],
  },
  {
    heading: "7. Data Security",
    paragraphs: [
      "We take reasonable technical, administrative, and organisational measures to protect personal information against unauthorised access, misuse, alteration, disclosure, or destruction.",
      "However, no electronic transmission, website, or storage system can be guaranteed to be completely secure.",
    ],
  },
  {
    heading: "8. Data Retention",
    paragraphs: [
      "We retain personal information only for as long as reasonably necessary for the purposes described in this Privacy Policy, including providing services, maintaining business and client records, resolving disputes, complying with legal obligations, and protecting our legitimate business interests.",
    ],
  },
  {
    heading: "9. Your Rights",
    paragraphs: [
      "Subject to applicable law, you may have rights relating to your personal information, including the right to request access, correction, updating, or deletion of certain information.",
      "If you wish to exercise any applicable rights or have questions regarding your personal information, you may contact us using the details below.",
    ],
  },
  {
    heading: "10. Third-Party Links",
    paragraphs: [
      "Our website may contain links to third-party websites, social media platforms, or external services.",
      "HashTag Marketing is not responsible for the privacy practices, content, security, or policies of such third-party platforms.",
      "We recommend reviewing the privacy policies of those websites before providing personal information.",
    ],
  },
  {
    heading: "11. Children's Privacy",
    paragraphs: [
      "Our website is not intended to knowingly collect personal information from children.",
      "If you believe that a child has provided personal information to us without appropriate consent, please contact us so that we can take appropriate action.",
    ],
  },
  {
    heading: "12. Changes to This Privacy Policy",
    paragraphs: [
      "HashTag Marketing may update this Privacy Policy from time to time to reflect changes in our services, business practices, technology, or applicable legal requirements.",
    ],
  },
  {
    heading: "13. Contact Us",
    paragraphs: ["If you have any questions, concerns, or requests regarding this Privacy Policy, please contact:"],
    contact: true,
  },
];

const COOKIES_SECTIONS = [
  {
    heading: "1. What Are Cookies?",
    paragraphs: [
      "Cookies are small text files that are placed on your device when you visit a website.",
      "They allow websites to recognise your device, remember certain preferences, understand how visitors interact with the website, and provide or measure certain features and services.",
    ],
  },
  {
    heading: "2. Why We Use Cookies",
    paragraphs: ["HashTag Marketing may use cookies and similar technologies to:"],
    bullets: [
      "Enable essential website functions.",
      "Remember preferences and settings.",
      "Understand how visitors navigate and interact with our website.",
      "Measure website traffic and performance.",
      "Improve website functionality and user experience.",
      "Understand the effectiveness of marketing campaigns.",
      "Measure advertising and campaign performance.",
      "Support relevant marketing and advertising activities, where applicable.",
      "Identify and address security issues or misuse.",
    ],
  },
  {
    heading: "3. Types of Cookies We May Use",
    defs: [
      { term: "Essential Cookies", desc: "These cookies are necessary for certain parts of the website to function properly." },
      { term: "Analytics Cookies", desc: "These cookies help us understand how visitors use our website, including which pages are visited, how visitors navigate the website, and how the website performs." },
      { term: "Functional Cookies", desc: "These cookies may remember preferences and settings to provide a more convenient and personalised experience." },
      { term: "Marketing and Advertising Cookies", desc: "These cookies may be used to measure marketing campaigns, understand advertising performance, or support relevant advertising and marketing activities." },
    ],
  },
  {
    heading: "4. Third-Party Cookies",
    paragraphs: [
      "Our website may use third-party services that place cookies or similar technologies on your device.",
      "These may include:",
    ],
    bullets: [
      "Analytics services",
      "Advertising platforms",
      "Social media platforms",
      "Embedded content",
      "Website and performance tools",
      "Marketing and campaign technologies",
    ],
    after: ["Third-party providers may collect and process information according to their own privacy policies and terms."],
  },
  {
    heading: "5. Managing Cookies",
    paragraphs: [
      "You can manage or disable cookies through your browser settings.",
      "Where available, you may also manage certain cookie preferences through the cookie consent options provided on our website.",
      "Please note that disabling certain cookies may affect the functionality, performance, or user experience of parts of the website.",
    ],
  },
  {
    heading: "6. Changes to This Cookie Policy",
    paragraphs: [
      "HashTag Marketing may update this Cookie Policy from time to time to reflect changes in our website, technology, services, or applicable legal requirements.",
    ],
  },
  {
    heading: "7. Contact Us",
    paragraphs: ["If you have any questions about our use of cookies, please contact:"],
    contact: true,
  },
];

const COPY = {
  privacy: {
    title: "Privacy Policy",
    lines: ["Privacy", "policy."],
    intro: "How we collect, use and protect information submitted through this website.",
    introParagraphs: [
      "At HashTag Marketing, we respect your privacy and are committed to protecting the personal information you share with us.",
      "HashTag Marketing is a 360\u00b0 digital marketing and communications agency offering services across social media management, digital marketing, branding, creative services, public relations, influencer marketing and outreach, content creation, website development and branding, campaign management, marketing strategy, and related services.",
      "This Privacy Policy explains how we collect, use, store, and protect information when you visit our website, contact us, submit an enquiry, engage with our services, or otherwise interact with HashTag Marketing.",
      "By using our website, you acknowledge and agree to the practices described in this Privacy Policy.",
    ],
    sections: PRIVACY_SECTIONS,
  },
  cookies: {
    title: "Cookie Policy",
    lines: ["Cookie", "policy."],
    intro: "How this website uses cookies and similar technologies.",
    introParagraphs: [
      "This Cookie Policy explains how HashTag Marketing uses cookies and similar technologies when you visit our website.",
      "As a 360\u00b0 digital marketing and communications agency, we use certain technologies to help our website function effectively, understand how visitors use our website, improve user experience, measure marketing performance, and enhance our digital activities.",
    ],
    sections: COOKIES_SECTIONS,
  },
  terms: {
    title: "Terms & Conditions",
    lines: ["Terms &", "Conditions."],
    intro: "These Terms & Conditions govern your access to and use of the HashTag Marketing website.",
    introParagraphs: [
      "Welcome to the website of HashTag Marketing. These Terms & Conditions govern your access to and use of the HashTag Marketing website.",
      "HashTag Marketing is a 360\u00b0 digital marketing and communications agency providing services including social media management, digital marketing, public relations, influencer marketing and outreach, branding, creative services, website development and branding, content creation, campaign strategy, digital advertising, and marketing consultancy.",
      "By accessing or using this website, you agree to these Terms & Conditions. If you do not agree with these terms, please discontinue use of the website.",
    ],
    sections: TERMS_SECTIONS,
  },
};

function Contact() {
  return (
    <div className="mt-3 space-y-1 text-base leading-relaxed text-ink">
      <p className="font-semibold">HashTag Marketing</p>
      <p>
        Email:{" "}
        <a href={`mailto:${brand.email}`} className="text-accent underline-offset-4 hover:underline" data-testid="terms-contact-email">
          {brand.email}
        </a>
      </p>
      <p>
        Phone:{" "}
        <a href={brand.phoneHref} className="text-accent underline-offset-4 hover:underline" data-testid="terms-contact-phone">
          {brand.phone}
        </a>
      </p>
      <p>Address: {brand.location}</p>
    </div>
  );
}

export default function Legal({ kind }) {
  const c = COPY[kind] || COPY.privacy;
  useSeo({ title: c.title, description: c.intro, path: `/${kind}`, breadcrumbs: [{ name: c.title, path: `/${kind}` }] });
  return (
    <>
      <PageHeader eyebrow="Legal" titleLines={c.lines} intro={c.intro} />
      <section className="mx-auto max-w-3xl px-5 py-14 sm:py-16 md:py-20 lg:py-24 xl:py-28 sm:px-8 md:px-10 lg:px-12 xl:px-16">
        <div className="space-y-10" data-testid={`${kind}-content`}>
          {c.introParagraphs && (
            <div className="border-b border-line pb-6">
              {c.introParagraphs.map((p, i) => (
                <p key={i} className={`${i === 0 ? "" : "mt-4 "}text-base leading-relaxed text-muted`}>
                  {p}
                </p>
              ))}
            </div>
          )}
          {c.sections.map((s) => (
            <div key={s.heading} className="space-y-3">
              <h2 className="font-display text-xl font-bold text-ink sm:text-2xl">{s.heading}</h2>
              {s.paragraphs?.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-muted">
                  {p}
                </p>
              ))}
              {s.bullets && (
                <ul className="ml-5 list-disc space-y-1.5 text-base leading-relaxed text-muted marker:text-accent">
                  {s.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
              {s.after?.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-muted">
                  {p}
                </p>
              ))}
              {s.bullets2 && (
                <ul className="ml-5 list-disc space-y-1.5 text-base leading-relaxed text-muted marker:text-accent">
                  {s.bullets2.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
              {s.defs && (
                <div className="space-y-4">
                  {s.defs.map((d, i) => (
                    <div key={i}>
                      <p className="font-semibold text-ink">{d.term}</p>
                      <p className="mt-1 text-base leading-relaxed text-muted">{d.desc}</p>
                    </div>
                  ))}
                </div>
              )}
              {s.contact && <Contact />}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
